import { useMemo, useState } from "react";
import DataEntryKpiSchoolsModal from "@/components/DataEntryKpiSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Repeat,
  ShieldCheck,
  User,
} from "lucide-react";
import { BlockLink } from "@/components/DrilldownLink";
import DataEntryGuidanceChip from "@/components/DataEntryGuidanceChip";
import {
  buildActionQueue,
  buildAttentionBlocks,
  buildClosurePlan,
  buildCompletionDonutStyle,
  buildHeroNarrative,
  buildPrimaryAction,
  buildReadinessGaugeStyle,
  buildRingStyle,
  computeExecutiveReadiness,
  fmt,
  fmtLakhs,
  getAttentionBlockSubtitle,
  getTopBlockSubtitle,
  maxBandWidth,
  pct,
  round2,
} from "@/utils/dataEntryDerived";
import { resolveAiList, resolveAiText } from "@/utils/resolveAiGuidance";

const bandMeta = [
  { key: "100%", label: "100%", tone: "" },
  { key: "95-99%", label: "95–99%", tone: "blue" },
  { key: "90-95%", label: "90–95%", tone: "orange" },
  { key: "<90%", label: "<90%", tone: "red" },
];

const DataEntryExecutiveTab = ({
  overview,
  completionBands,
  blockData,
  topBottomBlocks,
  guidance,
  guidanceProvider,
  guidanceLoading,
  onNavigateTab,
  scopeLabel = "Pune District",
}) => {
  const [kpiMetricModal, setKpiMetricModal] = useState(null);
  const readiness = computeExecutiveReadiness(overview);
  const nonRepeaterRate = round2(100 - (overview?.repeater_rate ?? 0));
  const inProgressRate = overview?.in_progress_rate ?? 0;
  const notStartedRate = overview?.not_started_rate ?? 0;

  const attentionBlocks = useMemo(() => buildAttentionBlocks(blockData), [blockData]);
  const localActionQueue = useMemo(() => buildActionQueue(overview, attentionBlocks), [overview, attentionBlocks]);
  const localClosurePlan = useMemo(() => buildClosurePlan(attentionBlocks), [attentionBlocks]);
  const actionQueue = resolveAiList(guidanceLoading, guidance?.action_queue, localActionQueue);
  const closurePlan = resolveAiList(guidanceLoading, guidance?.closure_plan, localClosurePlan);
  const heroNarrative =
    resolveAiText(guidanceLoading, guidance?.hero_narrative, buildHeroNarrative(overview)) ??
    "Generating AI insights…";
  const primaryAction =
    resolveAiText(guidanceLoading, guidance?.primary_action, buildPrimaryAction(overview)) ??
    "Generating AI insights…";
  const bandMax = maxBandWidth(completionBands);

  const scrollToActions = () => {
    if (onNavigateTab) onNavigateTab("insights");
    document.getElementById("de-action-queue")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToQuality = () => {
    if (onNavigateTab) onNavigateTab("quality");
  };

  return (
    <div className="de-executive-tab" data-testid="data-entry-executive-tab">
      <section className="de-hero-card">
        <div className="de-hero-layout">
          <div>
            <div className="de-health-main">
              <div className="de-gauge" style={buildReadinessGaugeStyle(readiness)} aria-label={`Data readiness score ${readiness} percent`}>
                <div>
                  <strong>{pct(readiness, 1)}</strong>
                  <span>Readiness</span>
                </div>
              </div>
              <div className="de-health-copy">
                <h2>Data Entry Command Centre</h2>
                <p>{heroNarrative}</p>
                <div className="de-status-row">
                  <DataEntryGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                  <HeroStatusChip chipClass="de-status-chip" variant="green">
                    {pct(overview?.completion_rate ?? 0, 2)} entry complete
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="amber"
                    onClick={() => setKpiMetricModal("non_certified")}
                    ariaLabel="Certification rate — view non-certified schools"
                  >
                    {pct(overview?.certification_rate ?? 0, 1)} certified
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="blue"
                    onClick={() => setKpiMetricModal("pending_students")}
                    ariaLabel="Pending students — view schools with open records"
                  >
                    {fmt(overview?.pending_students ?? 0)} pending students
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="de-summary-note">
              <div className="de-icon-box orange">
                <Clock size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="de-metric-grid" aria-label="Executive KPIs">
            <article className="de-metric-card">
              <small>Total students</small>
              <strong>{fmt(overview?.total_students ?? 0)}</strong>
              <span>Across {fmt(overview?.total_schools ?? 0)} schools</span>
            </article>
            <article
              className="de-metric-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Completion rate: view schools below 100 percent completion"
              onClick={() => setKpiMetricModal("completion_rate")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setKpiMetricModal("completion_rate");
                }
              }}
            >
              <small>Completion rate</small>
              <strong>{pct(overview?.completion_rate ?? 0, 2)}</strong>
              <span>Entry almost complete</span>
            </article>
            <article
              className="de-metric-card warning clickable"
              role="button"
              tabIndex={0}
              aria-label="Pending students: view schools with open records"
              onClick={() => setKpiMetricModal("pending_students")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setKpiMetricModal("pending_students");
                }
              }}
            >
              <small>Pending students</small>
              <strong>{fmt(overview?.pending_students ?? 0)}</strong>
              <span>{pct(overview?.pending_rate ?? 0, 2)} pending</span>
            </article>
            <article
              className="de-metric-card danger clickable"
              role="button"
              tabIndex={0}
              aria-label="Non-certified: view schools awaiting certification"
              onClick={() => setKpiMetricModal("non_certified")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setKpiMetricModal("non_certified");
                }
              }}
            >
              <small>Non-certified</small>
              <strong>{fmt(overview?.non_certified_schools ?? 0)}</strong>
              <span>Needs officer action</span>
            </article>
          </div>
        </div>
      </section>

      <div className="de-section-title">
        <div>
          <h2>Executive KPI Snapshot</h2>
          <p>Key metrics are grouped by decision relevance: completion, school closure, certification, and repeater validation.</p>
        </div>
        <button type="button" className="de-section-action" onClick={scrollToActions}>
          View action queue →
        </button>
      </div>

      <section className="de-kpi-strip">
        <article className="de-mini-kpi">
          <div className="de-mini-head">
            <small>100% complete schools</small>
            <span className="de-icon-box green">
              <CheckCircle2 size={22} />
            </span>
          </div>
          <strong>{pct(overview?.full_completion_rate ?? 0, 1)}</strong>
          <p>
            <span className="trend">{fmt(overview?.full_completion_schools ?? 0)}</span> schools fully completed
          </p>
        </article>
        <article className="de-mini-kpi">
          <div className="de-mini-head">
            <small>Certified schools</small>
            <span className="de-icon-box red">
              <ShieldCheck size={22} />
            </span>
          </div>
          <strong>{pct(overview?.certification_rate ?? 0, 1)}</strong>
          <p>
            <span className="bad">{fmt(overview?.certified_schools ?? 0)}</span> of {fmt(overview?.total_schools ?? 0)} certified
          </p>
        </article>
        <article className="de-mini-kpi">
          <div className="de-mini-head">
            <small>Average students/school</small>
            <span className="de-icon-box">
              <User size={22} />
            </span>
          </div>
          <strong>{fmt(overview?.avg_students_per_school ?? 0)}</strong>
          <p>Useful for school audit load planning</p>
        </article>
        <article className="de-mini-kpi">
          <div className="de-mini-head">
            <small>Repeater rate</small>
            <span className="de-icon-box purple">
              <Repeat size={22} />
            </span>
          </div>
          <strong>{pct(overview?.repeater_rate ?? 0, 2)}</strong>
          <p>
            <span className="warn">{fmt(overview?.total_repeaters ?? 0)}</span> students flagged for review
          </p>
        </article>
      </section>

      <div className="de-section-title">
        <div>
          <h2>Completion and Certification Diagnostics</h2>
          <p>Visuals are redesigned to make the small pending gap and the large certification gap visible at the same time.</p>
        </div>
      </div>

      <section className="de-analytics-grid">
        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Entry Status Distribution</h3>
              <p>Completed versus pending student records.</p>
            </div>
            <span className="de-pill">Student records</span>
          </div>
          <div className="de-completion-card">
            <div
              className="de-donut-large"
              style={buildCompletionDonutStyle(
                overview?.completion_rate ?? 0,
                inProgressRate,
                notStartedRate
              )}
            >
              <div>
                <strong>{pct(overview?.completion_rate ?? 0, 2)}</strong>
                <span>Complete</span>
              </div>
            </div>
            <div className="de-legend-list">
              <div className="de-legend-card">
                <div>
                  <b>
                    <i className="de-dot" />
                    Completed records
                  </b>
                  <span>Validated student entries</span>
                </div>
                <div className="de-legend-value">{fmtLakhs(overview?.total_completed ?? 0)}</div>
              </div>
              <div className="de-legend-card">
                <div>
                  <b>
                    <i className="de-dot warn" />
                    In progress
                  </b>
                  <span>Minor school-level closure work</span>
                </div>
                <div className="de-legend-value">
                  {(overview?.total_in_progress ?? 0) >= 1000
                    ? `~${Math.round((overview?.total_in_progress ?? 0) / 1000)}K`
                    : fmt(overview?.total_in_progress ?? 0)}
                </div>
              </div>
              <div className="de-legend-card">
                <div>
                  <b>
                    <i className="de-dot bad" />
                    Pending records
                  </b>
                  <span>Requires data entry follow-up</span>
                </div>
                <div className="de-legend-value">{fmt(overview?.pending_students ?? 0)}</div>
              </div>
            </div>
          </div>
        </article>

        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>School Completion Bands</h3>
              <p>School count by completion percentage.</p>
            </div>
            <span className="de-pill">{fmt(overview?.total_schools ?? 0)} schools</span>
          </div>
          <div className="de-bar-list" aria-label="School completion bands">
            {bandMeta.map(({ key, label, tone }) => {
              const value = completionBands?.[key] ?? 0;
              const width = bandMax ? Math.max((value / bandMax) * 100, value > 0 ? 4 : 0) : 0;
              return (
                <div className="de-hbar-row" key={key}>
                  <div className="de-hbar-label">{label}</div>
                  <div className="de-hbar-track">
                    <div className={`de-hbar ${tone}`} style={{ width: `${width}%` }} />
                  </div>
                  <div className="de-hbar-value">{fmt(value)}</div>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="de-gauge-row">
        <article className="de-ring-card">
          <div className="de-ring" style={buildRingStyle(overview?.completion_rate ?? 0, "green")}>
            <strong>{pct(overview?.completion_rate ?? 0, 2)}</strong>
          </div>
          <b>Data Entry Completion</b>
          <span>Student-level entry status</span>
        </article>
        <article className="de-ring-card">
          <div className="de-ring orange" style={buildRingStyle(overview?.full_completion_rate ?? 0, "orange")}>
            <strong>{pct(overview?.full_completion_rate ?? 0, 1)}</strong>
          </div>
          <b>Fully Completed Schools</b>
          <span>Schools at 100% completion</span>
        </article>
        <article className="de-ring-card">
          <div className="de-ring red" style={buildRingStyle(overview?.certification_rate ?? 0, "red")}>
            <strong>{pct(overview?.certification_rate ?? 0, 1)}</strong>
          </div>
          <b>School Certification</b>
          <span>Officer-certified schools</span>
        </article>
        <article className="de-ring-card">
          <div className="de-ring orange" style={buildRingStyle(nonRepeaterRate, "orange")}>
            <strong>{pct(nonRepeaterRate, 2)}</strong>
          </div>
          <b>Non-Repeater Rate</b>
          <span>Records without repeater flag</span>
        </article>
      </section>

      <div className="de-section-title">
        <div>
          <h2>Block Performance and Attention List</h2>
          <p>Top performers and blocks needing attention are shown in separate cards with consistent ranking patterns and clear completion gaps.</p>
        </div>
        <button type="button" className="de-section-action" onClick={scrollToQuality}>
          View quality KPIs ↑
        </button>
      </div>

      <section className="de-two-column">
        <article className="de-panel de-ranking-card">
          <div className="de-ranking-head">
            <div>
              <h3>Top Performing Blocks</h3>
              <p>Highest data completion consistency.</p>
            </div>
            <span className="de-pill">Best 5</span>
          </div>
          <div className="de-rank-list">
            {(topBottomBlocks?.top_blocks ?? []).map((block, idx) => (
              <div className="de-rank-row" key={block.block_name}>
                <span className="de-rank-no">{idx + 1}</span>
                <div>
                  <b>{block.block_name}</b>
                  <span>{getTopBlockSubtitle(block, idx)}</span>
                </div>
                <span className="de-score-badge">{pct(block.completion_rate, 2)}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="de-panel de-ranking-card">
          <div className="de-ranking-head">
            <div>
              <h3>Blocks Needing Attention</h3>
              <p>Completion is high, but these blocks have the largest remaining gaps.</p>
            </div>
            <span className="de-pill">Priority 5</span>
          </div>
          <div className="de-rank-list">
            {(topBottomBlocks?.bottom_blocks ?? []).map((block, idx) => (
              <div className="de-rank-row alert" key={block.block_name}>
                <span className="de-rank-no">{idx + 1}</span>
                <div>
                  <b>{block.block_name}</b>
                  <span>
                    {getAttentionBlockSubtitle({
                      ...block,
                      pending_students: blockData?.find((b) => b.block_name === block.block_name)?.pending_students,
                      certification_rate: blockData?.find((b) => b.block_name === block.block_name)?.certification_rate,
                      total_schools: block.total_schools,
                    })}
                  </span>
                </div>
                <span className="de-score-badge">{pct(block.completion_rate, 2)}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="de-panel de-table-card">
        <div className="de-table-head">
          <div>
            <h3>Block-Wise Executive Monitoring Table</h3>
            <p>Designed for review meetings: completion, pending records, certification load and next action.</p>
          </div>
          <span className="de-pill">Priority monitoring</span>
        </div>
        <div className="de-table-wrap">
          <table className="de-table" aria-label="Block-wise executive monitoring table">
            <thead>
              <tr>
                <th>Block</th>
                <th>Completion</th>
                <th>Pending Students</th>
                <th>Fully Complete Schools</th>
                <th>Certified Schools</th>
                <th>Repeater Rate</th>
                <th>Priority</th>
                <th>Next Action</th>
              </tr>
            </thead>
            <tbody>
              {attentionBlocks.map((block) => (
                <tr key={block.block_name}>
                  <td>
                    <BlockLink blockCode={block.block_code} blockName={block.block_name} className="de-num-blue">
                      {block.block_name}
                    </BlockLink>
                  </td>
                  <td>{pct(block.completion_pct ?? 0, 2)}</td>
                  <td>{fmt(block.pending_students ?? 0)}</td>
                  <td>{pct(block.full_completion_rate ?? 0, 1)}</td>
                  <td>{pct(block.certification_rate ?? 0, 1)}</td>
                  <td>{pct(block.repeater_rate ?? 0, 1)}</td>
                  <td>
                    <span className={`de-tag ${block.priority.tone}`}>{block.priority.label}</span>
                  </td>
                  <td>{block.next_action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="de-section-title" id="de-action-queue">
        <div>
          <h2>Officer Action Queue</h2>
          <p>The executive screen now ends with clear ownership actions instead of only passive charts.</p>
        </div>
      </div>

      <section className="de-action-grid">
        {actionQueue.map((item) => (
          <article className="de-action-item" key={item.title}>
            <div className={`de-icon-box ${item.tone}`}>
              {item.tone === "red" ? (
                <AlertTriangle size={22} />
              ) : item.tone === "orange" ? (
                <Clock size={22} />
              ) : (
                <Repeat size={22} />
              )}
            </div>
            <div>
              <h4>{item.title}</h4>
              <p>{item.body}</p>
              <small>{item.footer}</small>
            </div>
          </article>
        ))}
      </section>

      <section className="de-panel">
        <div className="de-panel-head">
          <div>
            <h3>30-Day Data Governance Closure Plan</h3>
            <p>Four-step cadence for moving from entry completion to certified, auditable data.</p>
          </div>
          <span className="de-pill">Execution plan</span>
        </div>
        <div className="de-plan-grid">
          {closurePlan.map((step) => (
            <div className="de-plan-card" key={step.week}>
              <b>{step.week}</b>
              <span>{step.text}</span>
            </div>
          ))}
        </div>
      </section>

      <DataEntryKpiSchoolsModal
        open={Boolean(kpiMetricModal)}
        metric={kpiMetricModal}
        onClose={() => setKpiMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          kpiMetricModal === "completion_rate"
            ? overview?.completion_rate
            : kpiMetricModal === "pending_students"
              ? overview?.pending_rate
              : kpiMetricModal === "non_certified"
                ? overview?.certification_rate
                : null
        }
        districtAggregate={kpiMetricModal === "pending_students" ? overview?.pending_students : null}
      />
    </div>
  );
};

export default DataEntryExecutiveTab;
